import React, { useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { CircularProgress, IconButton } from '@mui/material'
import { SkeletonRows } from 'components/Skeleton'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  listWebhookSubscriptions,
  createWebhookSubscription,
  deleteWebhookSubscription,
} from 'services/webhookService'
import SearchableSelect from 'components/SearchableSelect'

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
  padding: 16px;
`

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  max-width: 620px;
  width: 100%;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const Body = styled.div`
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const Intro = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#475569'};
`

const SubsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SubRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  background: #fff;
`

const SubBody = styled.div`
  flex: 1;
  min-width: 0;
`

const SubName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  display: flex;
  align-items: center;
  gap: 6px;
`

const SubUrl = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  font-family: 'SFMono-Regular', Menlo, monospace;
  word-break: break-all;
  margin-top: 2px;
`

const EventChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`

const Chip = styled.span`
  font-size: 10px;
  background: #e0e7ff;
  color: #3730a3;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 500;
  letter-spacing: 0.02em;
`

const FormatChip = styled.span`
  font-size: 10px;
  background: ${({ $f }) => $f === 'slack' ? '#dcfce7' : $f === 'teams' ? '#e0f2fe' : '#f3f4f6'};
  color: ${({ $f }) => $f === 'slack' ? '#15803d' : $f === 'teams' ? '#075985' : '#475569'};
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.04em;
`

const LastDeliveryRow = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const Empty = styled.div`
  background: ${({ theme }) => theme.colors.backgroundColor};
  border: 1px dashed ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 18px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  background: #0e7490;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover { opacity: 0.92; }
`

const NewForm = styled.form`
  background: ${({ theme }) => theme.colors.backgroundColor};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Label = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  outline: none;
  &:focus { border-color: #0e7490; }
`

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
`

const EventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 4px;
`

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const Btn = styled.button`
  border: 1px solid ${({ $primary, theme }) => $primary ? '#0e7490' : theme.colors.lightGrey};
  background: ${({ $primary }) => $primary ? '#0e7490' : '#fff'};
  color: ${({ $primary, theme }) => $primary ? '#fff' : theme.colors.haiti};
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: ${({ $primary }) => $primary ? 600 : 500};
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const ErrMsg = styled.div`
  background: #fee2e2;
  color: #b91c1c;
  font-size: 12px;
  padding: 8px 10px;
  border-radius: 6px;
`

const HelpText = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  margin-top: 2px;
`

const AVAILABLE_EVENTS = [
  { value: 'task.overdue',              label: 'Task overdue' },
  { value: 'task.deadline-approaching', label: 'Task deadline approaching (7/3/1d)' },
  { value: 'action.overdue',            label: 'Remedial overdue' },
  { value: 'incident.logged',           label: 'Incident logged' },
]

const WebhookSubscriptionsModal = ({ bpId, onClose }) => {
  const [subs, setSubs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [form, setForm] = useState({
    label: '',
    targetUrl: '',
    format: 'slack',
    events: ['task.overdue'],
  })
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listWebhookSubscriptions(bpId)
      setSubs(res.data?.data || [])
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not load webhook subscriptions.')
    } finally {
      setLoading(false)
    }
  }, [bpId])

  useEffect(() => { load() }, [load])

  const toggleEvent = (e) => {
    setForm(f => {
      const has = f.events.includes(e)
      return { ...f, events: has ? f.events.filter(x => x !== e) : [...f.events, e] }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    setError(null)
    if (!form.label.trim()) { setError('Label is required.'); return }
    if (!/^https:\/\//.test(form.targetUrl)) { setError('Target URL must start with https://'); return }
    if (form.events.length === 0) { setError('Select at least one event.'); return }
    setSubmitting(true)
    try {
      await createWebhookSubscription(bpId, {
        label: form.label.trim(),
        targetUrl: form.targetUrl.trim(),
        format: form.format,
        eventFilters: form.events,
      })
      toast.success('Webhook subscription created.')
      setForm({ label: '', targetUrl: '', format: 'slack', events: ['task.overdue'] })
      setShowForm(false)
      await load()
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not create subscription.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (sub) => {
    if (busyId) return
    if (!window.confirm(`Delete webhook "${sub.label}"?`)) return
    setBusyId(sub.id)
    try {
      await deleteWebhookSubscription(bpId, sub.id)
      toast.success('Webhook subscription deleted.')
      await load()
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not delete this subscription.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>Webhook subscriptions</Title>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Header>

        <Body>
          <Intro>
            POST events from this Building Passport to Slack, Microsoft Teams, or any HTTPS endpoint.
            Subscriptions are scoped to this Building Passport only. They fire alongside the in-app and email notifications.
          </Intro>

          {loading ? <SkeletonRows rows={4} /> : subs.length === 0 ? (
            <Empty>No subscriptions yet.</Empty>
          ) : (
            <SubsList>
              {subs.map(s => (
                <SubRow key={s.id}>
                  <SubBody>
                    <SubName>
                      {s.label}
                      <FormatChip $f={s.format}>{s.format}</FormatChip>
                      {!s.isActive && <FormatChip $f="generic">paused</FormatChip>}
                    </SubName>
                    <SubUrl>{s.targetUrl}</SubUrl>
                    <EventChips>
                      {(s.eventFilters || []).map(e => (
                        <Chip key={e}>{e}</Chip>
                      ))}
                    </EventChips>
                    {s.lastDeliveredAt && (
                      <LastDeliveryRow>
                        {s.lastStatus === 'ok'
                          ? <CheckCircleOutlineIcon style={{ fontSize: 13, color: '#16a34a' }} />
                          : <ErrorOutlineIcon style={{ fontSize: 13, color: '#dc2626' }} />}
                        Last delivery {new Date(s.lastDeliveredAt).toLocaleString()}
                        {s.lastError && `: ${s.lastError.slice(0, 80)}`}
                      </LastDeliveryRow>
                    )}
                  </SubBody>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(s)}
                    disabled={busyId === s.id}
                    title="Delete"
                  >
                    {busyId === s.id
                      ? <CircularProgress size={12} />
                      : <DeleteOutlineIcon fontSize="small" />}
                  </IconButton>
                </SubRow>
              ))}
            </SubsList>
          )}

          {!showForm ? (
            <AddBtn onClick={() => setShowForm(true)}>
              <AddIcon style={{ fontSize: 16 }} /> Add subscription
            </AddBtn>
          ) : (
            <NewForm onSubmit={handleSubmit}>
              <Row>
                <Label>Label *</Label>
                <Input
                  value={form.label}
                  onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. #ops-alerts (Slack)"
                />
              </Row>
              <Row>
                <Label>Target URL * (https only)</Label>
                <Input
                  type="url"
                  value={form.targetUrl}
                  onChange={e => setForm(f => ({ ...f, targetUrl: e.target.value }))}
                  placeholder="https://hooks.slack.com/services/…"
                />
                <HelpText>
                  Slack: incoming-webhook URL. Teams: connector webhook URL.
                  Other endpoints: generic JSON POST.
                </HelpText>
              </Row>
              <Row>
                <Label>Payload format</Label>
                <SearchableSelect
                  value={form.format}
                  onChange={v => setForm(f => ({ ...f, format: v }))}
                  options={[
                    { value: 'slack', label: 'Slack blocks' },
                    { value: 'teams', label: 'Teams MessageCard' },
                    { value: 'generic', label: 'Generic JSON' },
                  ]}
                  ariaLabel="Payload format"
                />
              </Row>
              <Row>
                <Label>Events to fire on</Label>
                <EventList>
                  {AVAILABLE_EVENTS.map(ev => (
                    <CheckboxRow key={ev.value}>
                      <input
                        type="checkbox"
                        checked={form.events.includes(ev.value)}
                        onChange={() => toggleEvent(ev.value)}
                      />
                      {ev.label}
                    </CheckboxRow>
                  ))}
                </EventList>
              </Row>

              {error && <ErrMsg>{error}</ErrMsg>}

              <FormFooter>
                <Btn type="button" onClick={() => { setShowForm(false); setError(null) }} disabled={submitting}>Cancel</Btn>
                <Btn type="submit" $primary disabled={submitting}>
                  {submitting ? 'Creating…' : 'Create subscription'}
                </Btn>
              </FormFooter>
            </NewForm>
          )}
        </Body>
      </Modal>
    </Backdrop>
  )
}

export default WebhookSubscriptionsModal
